import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";
import type { ScheduleChangeView } from "../../shared/types/api";
import { CHANGE_TYPE_LABELS, REASON_LABELS, formatDays } from "./changeVocabulary";

/**
 * Signing off — or declining — a commitment that moved outwards (§57).
 *
 * <p>The change is already recorded and the item's dates have already moved; what is being
 * decided is whether the new commitment stands. Recording first and deciding second is
 * deliberate: a slip that is refused sign-off still happened, and a system that only kept the
 * approved ones would report a project that never slipped.
 */
export function ApprovalDrawer({
  change,
  onClose,
  onSaved,
}: {
  change: ScheduleChangeView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (change) {
      setNote("");
      setError(null);
    }
  }, [change]);

  const decide = async (outcome: "approve" | "reject") => {
    if (!change) {
      return;
    }
    if (outcome === "reject" && note.trim() === "") {
      setError("Say why it is being declined — that is all the person will see.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/schedule-changes/${change.id}/${outcome}`, { note: note || null });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that decision.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(change)}
      onClose={onClose}
      title="Sign off this change"
      subtitle={change ? `${change.workItemKey} — ${change.workItemTitle}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void decide("reject")} disabled={saving}>
            Decline
          </Button>
          <Button onClick={() => void decide("approve")} disabled={saving}>
            Approve
          </Button>
        </div>
      }
    >
      {change && (
        <div className="space-y-4">
          {error && <InlineAlert tone="error">{error}</InlineAlert>}

          <div className="overflow-hidden rounded-lg border border-ink-200 text-sm">
            <Row label="What moved" value={CHANGE_TYPE_LABELS[change.changeType]} />
            <Row label="From" value={change.oldDate ?? "not set"} />
            <Row label="To" value={change.newDate} />
            <Row label="That is" value={formatDays(change.slippageDays)} />
            <Row label="Reason" value={REASON_LABELS[change.reasonCode]} />
            <Row label="Moved by" value={change.changedByName} />
          </div>

          {change.reasonComment && (
            <div className="rounded-lg bg-ink-50 px-3 py-2.5">
              <p className="text-xs text-ink-500">In their words</p>
              <p className="mt-0.5 text-sm text-ink-800">{change.reasonComment}</p>
            </div>
          )}

          {change.impactDescription && (
            <div className="rounded-lg bg-ink-50 px-3 py-2.5">
              <p className="text-xs text-ink-500">Impact</p>
              <p className="mt-0.5 text-sm text-ink-800">
                {change.impactDescription}
                {change.impactHours != null && ` (${change.impactHours}h)`}
              </p>
            </div>
          )}

          <InlineAlert tone="info">
            Declining does not put the date back. The change is on record either way — what this
            decides is whether the new commitment is agreed.
          </InlineAlert>

          <Field label="Note" hint="Required when declining, and kept with the record.">
            <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </div>
      )}
    </Drawer>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 last:border-0">
      <span className="text-ink-500">{label}</span>
      <span className="text-ink-900">{value}</span>
    </div>
  );
}
